import { useQuery, useMutation } from 'apollo-3';

import {
  GET_FEEDBACK_TEMPLATE_QUERY,
  GET_FEEDBACK_DATA_QUERY,
  COMPONENT_TYPES,
} from './Feedback.query';
import { GIVE_FEEDBACK_MUTATION } from './Feedback.mutation';

const optimisticUpdateQuery = (
  proxy,
  { templateId, contentId, userId, contentMeta, contentType, data }
) => {
  // Read the data from our cache
  const feedbackData = proxy.readQuery({
    query: GET_FEEDBACK_DATA_QUERY,
    variables: { templateId, contentId, userId },
  });

  const { getFeedback } = feedbackData || {};
  const newFeedback = [{ data, contentMeta, contentType, __typename: 'Feedback' }, ...getFeedback];
  // Write our data back to the cache
  proxy.writeQuery({
    query: GET_FEEDBACK_DATA_QUERY,
    variables: { templateId, contentId, userId },
    data: {
      getFeedback: newFeedback,
    },
  });
};

const useGetFeedbackTemplate = ({ templateId, ...getFeedBackTemplateQueryOptions }) => {
  const { loading, error, data, ...queryUtils } = useQuery(GET_FEEDBACK_TEMPLATE_QUERY, {
    skip: !templateId,
    variables: { templateId },
    ...getFeedBackTemplateQueryOptions,
  });
  return { loading, error, data, isSuccess: data && !loading && !error, queryUtils };
};

const useGetFeedback = ({ templateId, contentId, userId, ...getFeedBackQueryOptions }) => {
  const { loading, error, data, ...queryUtils } = useQuery(GET_FEEDBACK_DATA_QUERY, {
    variables: { templateId, userId, contentId },
    skip: !userId || !templateId || !contentId,
    ...getFeedBackQueryOptions,
  });
  return { loading, error, data, isSuccess: data && !loading && !error, queryUtils };
};

const useGiveFeedback = ({ templateId, ...giveFeedBackOptions }) => {
  const [handleGiveFeedback, { loading, error, data, ...mutateUtils }] = useMutation(
    GIVE_FEEDBACK_MUTATION,
    {
      ...giveFeedBackOptions,
    }
  );

  const giveFeedback = (
    contentId,
    userId,
    contentMeta = null,
    contentType,
    data,
    { shouldUpdate = false } = {}
  ) => {
    handleGiveFeedback({
      variables: { templateId, contentId, userId, contentMeta, contentType, data },
      update: (proxy) => {
        if (shouldUpdate) {
          optimisticUpdateQuery(proxy, {
            templateId,
            contentId,
            userId,
            contentMeta,
            contentType,
            data,
          });
        }
      },
    });
  };

  return {
    giveFeedback,
    loading,
    error,
    data,
    isSuccess: data && !loading && !error,
    mutateUtils,
  };
};

export { useGetFeedbackTemplate, useGetFeedback, useGiveFeedback, COMPONENT_TYPES };
